import { motion } from "framer-motion";
import {
  Building2,
  Calculator,
  Landmark,
  Home,
  FileCheck,
  Settings,
  TrendingUp,
  ArrowRight,
  Check,
} from "lucide-react";

const services = [
  {
    num: "01",
    icon: Building2,
    title: "Property Curation",
    subtitle: "Off-Market Access",
    hero: "We Source Premium Properties You Can't Find Online",
    description:
      "Most investors search public portals and miss the best opportunities. We access off-market, pre-launch properties through exclusive developer relationships — vetted for quality, location, and investment potential before you ever see them.",
    bullets: [
      "New construction & fully refurbished properties only",
      "Developer relationships for exclusive pre-market access",
      "Strict quality criteria: energy efficiency, location, design",
      "Investment potential validated before presentation",
    ],
    image: "https://images.unsplash.com/photo-1628792692983-b7e040e594dd?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
  },
  {
    num: "02",
    icon: Calculator,
    title: "Tax Structure",
    subtitle: "AfA Optimization",
    hero: "Maximize Every Euro of Depreciation Against Your Income",
    description:
      "German tax law offers powerful depreciation benefits most advisors overlook. Our in-house tax team models AfA deductions against your exact income bracket, ensuring you recover the maximum possible amount from the Finanzamt every year.",
    bullets: [
      "AfA depreciation modelling against your income bracket",
      "Interest deductibility optimization",
      "10-year exit strategy for tax-free capital gains",
      "Annual tax declaration support through our network",
    ],
    image: "https://images.unsplash.com/photo-1772588627327-1eeddcf73c11?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
  },
  {
    num: "03",
    icon: Landmark,
    title: "Financing",
    subtitle: "200+ Bank Partners",
    hero: "Optimal Financing From a Network of 200+ Institutions",
    description:
      "The right financing structure makes or breaks your investment. We negotiate across 200+ banks and financial institutions to secure the most favorable terms — rates, LTV ratios, and structures aligned to your long-term strategy.",
    bullets: [
      "Access to 200+ banks and financial institutions",
      "Optimal LTV ratios and interest rates",
      "Structure aligned to long-term investment strategy",
      "Portfolio financing for scaling investors",
    ],
    image: "https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
  },
  {
    num: "04",
    icon: Home,
    title: "Rental Strategy",
    subtitle: "Cash Flow Design",
    hero: "Rental Concepts Designed to Maximize Your Returns",
    description:
      "From flexible leasing to furnished rentals, we design cash-flow strategies tailored to your property and goals. With Berlin's 0.3% vacancy rate, tenant demand is guaranteed — we optimize how you capture it.",
    bullets: [
      "Flexible leasing, Airbnb-style, or fixed-rent models",
      "Built-in escalation clauses for inflation protection",
      "Full compliance with German rental laws",
      "Berlin vacancy rate: 0.3% — demand is guaranteed",
    ],
    image: "https://images.unsplash.com/photo-1758548157747-285c7012db5b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
  },
  {
    num: "05",
    icon: FileCheck,
    title: "Legal Execution",
    subtitle: "End-to-End",
    hero: "From Contract to Closing — Every Detail Managed",
    description:
      "Notary coordination, contract review, land register — every document translated, every deadline managed. We handle the entire legal process so you focus on the investment decision, not the bureaucracy.",
    bullets: [
      "Notary coordination and appointment management",
      "Purchase contract review in your language",
      "Land register and title verification",
      "All documents translated and explained",
    ],
    image: "https://images.unsplash.com/photo-1758518729240-7162d07427b8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
  },
  {
    num: "06",
    icon: Settings,
    title: "Management",
    subtitle: "Fully Handled",
    hero: "Full-Service Property Management, Zero Hassle",
    description:
      "From tenant sourcing to monthly reporting, we handle everything. You own the asset and receive the returns — we run the day-to-day operation so your investment is truly passive.",
    bullets: [
      "Tenant sourcing and screening",
      "Monthly rent collection and reporting",
      "Maintenance coordination and oversight",
      "24/7 support for every step of your journey",
    ],
    image: "https://images.unsplash.com/photo-1759238136818-7b00ec9e782a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
  },
  {
    num: "07",
    icon: TrendingUp,
    title: "Portfolio Growth",
    subtitle: "Strategic Scaling",
    hero: "Scale From One Property to a Diversified Portfolio",
    description:
      "Building a thriving portfolio requires strategic planning and market knowledge. We guide you through equity tracking, reinvestment timing, and diversified allocation across Germany's strongest markets.",
    bullets: [
      "Equity tracking and reinvestment timing",
      "Diversified asset allocation across locations",
      "Performance monitoring and market analysis",
      "Scale from 1 to 5+ properties strategically",
    ],
    image: "https://images.unsplash.com/photo-1681751864665-f07e8a462c49?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
  },
];

const ServicesPage = () => {
  return (
    <div className="min-h-screen bg-ire-black pt-32 pb-24" data-testid="services-page">
      {/* Hero */}
      <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24 mb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px w-16 bg-ire-accent" />
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">
              Our Services
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extralight text-white leading-[1.1] mb-6">
            Seven pillars.
            <br />
            <span className="text-white/40">One complete system.</span>
          </h1>
          <p className="font-body text-base lg:text-lg text-white/50 max-w-2xl leading-relaxed">
            Most advisors handle one or two aspects of a real estate acquisition.
            We handle all seven — from property sourcing to portfolio management —
            so you own a performing asset without lifting a finger.
          </p>
        </motion.div>
      </div>

      {/* Services */}
      {services.map((service, index) => {
        const Icon = service.icon;
        const isEven = index % 2 === 0;
        return (
          <div
            key={index}
            className={`py-20 ${index % 2 === 0 ? "bg-ire-black" : "bg-ire-surface"}`}
          >
            <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
              <div className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center`}>
                {/* Text */}
                <motion.div
                  initial={{ opacity: 0, x: isEven ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className={!isEven ? "lg:order-2" : ""}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-10 h-10 border border-ire-accent/30 bg-ire-accent/5 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-ire-accent" />
                    </div>
                    <span className="font-heading text-sm text-ire-accent">{service.num}</span>
                    <span className="font-body text-[10px] tracking-[0.15em] text-white/30 uppercase">{service.subtitle}</span>
                  </div>

                  <h2 className="font-heading text-2xl lg:text-3xl font-extralight text-white mb-5 leading-tight">
                    {service.hero}
                  </h2>

                  <p className="font-body text-sm text-white/45 leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <div className="space-y-2.5 mb-6">
                    {service.bullets.map((bullet, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check className="w-3.5 h-3.5 text-ire-accent flex-shrink-0 mt-0.5" />
                        <span className="font-body text-sm text-white/55">{bullet}</span>
                      </div>
                    ))}
                  </div>

                  <a
                    href="#consultation"
                    className="group inline-flex items-center gap-3 text-ire-accent font-body text-xs tracking-[0.15em] uppercase hover:gap-5 transition-all duration-500"
                  >
                    Discuss this with us
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </motion.div>

                {/* Image */}
                <motion.div
                  initial={{ opacity: 0, x: isEven ? 30 : -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className={`relative aspect-[4/3] overflow-hidden ${!isEven ? "lg:order-1" : ""}`}
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                  />
                  <div className="absolute inset-0 border border-white/[0.04]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ire-black/30 to-transparent" />
                </motion.div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Bottom CTA */}
      <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24 pt-24">
        <a
          href="#consultation"
          className="group relative flex items-center justify-between p-10 border border-ire-accent/20 bg-gradient-to-r from-ire-accent/[0.04] via-transparent to-ire-accent/[0.04] overflow-hidden hover:border-ire-accent/40 transition-all duration-700"
        >
          <div className="absolute -left-20 -top-20 w-60 h-60 bg-ire-accent/10 rounded-full blur-[80px] group-hover:bg-ire-accent/20 transition-all duration-700" />
          <div className="absolute -right-16 -bottom-16 w-48 h-48 bg-ire-accent/8 rounded-full blur-[60px] group-hover:bg-ire-accent/15 transition-all duration-700" />
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ire-accent/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ire-accent/60 to-transparent" />
          </div>
          <div className="relative z-10">
            <p className="font-body text-[10px] tracking-[0.3em] text-ire-accent/70 uppercase mb-2">Ready to get started?</p>
            <p className="font-heading text-2xl lg:text-3xl text-white group-hover:text-ire-accent transition-colors duration-500">Book Your Strategy Call</p>
          </div>
          <div className="relative z-10 w-14 h-14 border border-ire-accent/30 flex items-center justify-center group-hover:bg-ire-accent group-hover:border-ire-accent transition-all duration-500 shrink-0">
            <ArrowRight className="w-5 h-5 text-ire-accent group-hover:text-black group-hover:translate-x-1 transition-all duration-300" />
          </div>
        </a>
      </div>
    </div>
  );
};

export default ServicesPage;
